import React from 'react';
import { AvmResult, PropertyDataProps } from '../../utility/types';
import { DisplayList, ListItem } from '../common/DisplayList';
import {
  formatUsd,
  getLastSoldDate,
  getLastSoldPrice,
  getLastSoldPricePerSqFt,
  getPrice,
  getPricePerSqFt,
  isPropertyActive,
} from '../../utility/utility_functions';
import { NA } from '../../utility/constants';

const fallbackValue = (
  value: string | number,
  fallback: string,
  formatter: (value: string | number) => string
) => {
  if (value !== null && value !== undefined) return formatter(value);
  return fallback;
};

type PriceIncomeMetricsListProps = {
  propertyDetails: PropertyDataProps;
  soldAvmResult: AvmResult;
};

interface PriceIncomeListItem extends ListItem {
  forActive?: boolean;
}

const getDisplayItems = (
  propertyDetails: PropertyDataProps,
  soldAvmResult: AvmResult
): PriceIncomeListItem[] => {
  const price = getPrice(propertyDetails);
  const pricePerSqft = getPricePerSqFt(propertyDetails);
  const lastSoldPrice = getLastSoldPrice(propertyDetails);
  const lastSoldPricePerSqft = getLastSoldPricePerSqFt(propertyDetails);

  const items: PriceIncomeListItem[] = [
    {
      name: 'Potential Equity',
      value: fallbackValue(soldAvmResult?.potential_equity, NA, formatUsd),
      highlight: true,
    },
    {
      name: 'List Price',
      value: fallbackValue(price, NA, formatUsd),
      forActive: true,
    },
    {
      name: 'Price per Sq Ft',
      value: fallbackValue(pricePerSqft, NA, formatUsd),
      forActive: true,
    },
    {
      name: 'Last Sold Price',
      value: fallbackValue(lastSoldPrice, NA, formatUsd),
    },
    {
      name: `Last Sold Price per Sq Ft`,
      value: fallbackValue(lastSoldPricePerSqft, NA, formatUsd),
    },
    {
      name: 'Last Sold Date',
      value: getLastSoldDate(propertyDetails) ?? NA,
    },
    {
      name: 'AVM Value',
      value: fallbackValue(soldAvmResult?.estimated_value, NA, formatUsd),
    },
    {
      name: 'AVM Value per Sq Ft',
      value:
        soldAvmResult?.estimated_value !== undefined &&
        propertyDetails?.sqft > 0
          ? formatUsd(soldAvmResult.estimated_value / propertyDetails.sqft)
          : 'N/A',
    },
    {
      name: 'Tax Assessment Value',
      value: fallbackValue(propertyDetails?.assessed_value, NA, formatUsd),
    },
  ];

  if (isPropertyActive(propertyDetails)) return items;

  return items.filter((item) => !item?.forActive);
};

export const PriceIncomeMetricsList = ({
  propertyDetails,
  soldAvmResult,
}: PriceIncomeMetricsListProps) => {
  return (
    <DisplayList
      title="Price & Income Metrics"
      items={getDisplayItems(propertyDetails, soldAvmResult)}
    />
  );
};
